import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { formatDate } from "@/lib/format";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { X, Ticket, MapPin, LayoutGrid, List, Plus, Home } from "lucide-react";
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { SearchInput } from "@/components/ui/search-input";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { EventCard } from "@/components/EventCard";
import { lowestTicketTotal } from "@/lib/ticket-pricing";
import { LoadingSkeleton } from "@/components/LoadingSkeleton";
import { EmptyState } from "@/components/EmptyState";
import { SEOHead } from "@/components/SEOHead";
import { BecomeProducerModal } from "@/components/BecomeProducerModal";
import { useAuth } from "@/contexts/AuthContext";
import { EventFilterBar, defaultEventFilters, getDateRangeFromPreset, type EventFilters } from "@/components/EventFilterBar";
import { sanitizePostgrestFilter } from "@/lib/search";
import { useCityDetection } from "@/hooks/useCityDetection";
import { useDebounce } from "@/hooks/useDebounce";
import { isWithinInterval, endOfDay } from "date-fns";

export default function Eventos() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { role } = useAuth();
  const { t, i18n } = useTranslation("landingEvents");
  const [search, setSearch] = useState("");
  const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
  const debouncedSearch = useDebounce(search, 400);
  const { city, loading: cityLoading, requestLocation } = useCityDetection();
  const [producerModalOpen, setProducerModalOpen] = useState(false);
  const [cityFilter, setCityFilter] = useState<string>(searchParams.get("cidade") || "");

  const [filters, setFilters] = useState<EventFilters>(() => {
    const cat = searchParams.get("categoria") || "";
    return { ...defaultEventFilters, category: cat };
  });

  const handleCreateEvent = useCallback(() => {
    if (role === "producer") {
      navigate("/produtor/eventos/novo");
      return;
    }
    setProducerModalOpen(true);
  }, [role, navigate]);

  useEffect(() => {
    const cat = searchParams.get("categoria");
    const cid = searchParams.get("cidade");
    const q = searchParams.get("q");
    if (cat) setFilters((f) => ({ ...f, category: cat }));
    if (cid) setCityFilter(cid);
    if (q) setSearch(q);
  }, [searchParams]);

  const handleSearch = useCallback((value: string) => {
    setSearch(value);
  }, []);

  const PAGE_SIZE = 24;
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const loadMoreRef = useRef<HTMLDivElement>(null);

  // Limite inicial: 50 eventos para melhor performance
  // Se usuário quer ver mais, usa infinite scroll/pagination
  const INITIAL_LIMIT = 50;

  const { data: rawEvents, isLoading } = useQuery({
    queryKey: ["events", debouncedSearch, filters.category, cityFilter],
    queryFn: async () => {
      // Query única: events + preço mínimo via ticket_tiers aninhado
      // Elimina o segundo round-trip ao Supabase que existia antes
      const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();

      let query = supabase
        .from("events")
        .select("id, title, slug, start_date, end_date, venue_name, venue_city, cover_image_url, category, is_online, views_count, platform_fee_percent, profiles!events_producer_id_fkey(organizer_slug), organizations(slug)")
        .eq("status", "published")
        .eq("visibility", "public")
        .gte("start_date", oneDayAgo)
        .order("start_date", { ascending: true })
        .limit(INITIAL_LIMIT);

      if (debouncedSearch) {
        const safe = sanitizePostgrestFilter(debouncedSearch);
        query = query.or(`title.ilike.%${safe}%,description.ilike.%${safe}%,venue_name.ilike.%${safe}%,venue_city.ilike.%${safe}%`);
      }
      if (filters.category) query = query.eq("category", filters.category);
      if (cityFilter) query = query.eq("venue_city", cityFilter);

      const { data, error } = await query;
      if (error) throw error;

      const eventIds = (data || []).map(e => e.id);
      const { data: allTiers } = await supabase
        .from("ticket_tiers")
        .select("event_id, price, is_visible, name, absorb_fee")
        .in("event_id", eventIds);
        
      const tiersByEvent = (allTiers || []).reduce((acc: Record<string, any[]>, tier: any) => {
        if (!acc[tier.event_id]) acc[tier.event_id] = [];
        acc[tier.event_id].push(tier);
        return acc;
      }, {});

      return (data || []).map((e: any) => {
        const tiers = tiersByEvent[e.id] || [];
        const validTiers = tiers.filter((t: any) => 
          t.is_visible !== false && 
          t.name !== "Lista de Convidado" &&
          t.price !== null
        );
        
        const _minPrice = lowestTicketTotal(validTiers, e.platform_fee_percent);
        
        return { ...e, _minPrice };
      });
    },
  });

  // Reset visible count when filters change
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [debouncedSearch, filters, cityFilter]);

  // Apply all client-side filters in a single pass (O(n) instead of O(n*k))
  const filteredEvents = useMemo(() => {
    if (!rawEvents) return [];

    const datePresetRange = filters.datePreset ? getDateRangeFromPreset(filters.datePreset) : null;
    const hasDateRange = Boolean(filters.dateRange?.from);
    const dateRangeStart = filters.dateRange?.from;
    const dateRangeEnd = filters.dateRange?.to || filters.dateRange?.from;
    const priceMin = filters.priceMin ? Number(filters.priceMin) : null;
    const priceMax = filters.priceMax ? Number(filters.priceMax) : null;

    let result = rawEvents.filter((e: any) => {
      // Date preset filter
      if (datePresetRange && !isWithinInterval(new Date(e.start_date), { start: datePresetRange.start, end: datePresetRange.end })) {
        return false;
      }
      // Calendar range filter
      if (hasDateRange && !isWithinInterval(new Date(e.start_date), { start: dateRangeStart!, end: endOfDay(dateRangeEnd!) })) {
        return false;
      }
      // Price filters
      if (priceMin !== null && (e._minPrice ?? 0) < priceMin) return false;
      if (priceMax !== null && (e._minPrice ?? 0) > priceMax) return false;
      // Modality filter
      if (filters.modality === "online" && !e.is_online) return false;
      if (filters.modality === "presential" && e.is_online) return false;
      return true;
    });

    // Sort after filtering
    if (filters.sort === "relevance") {
      result.sort((a: any, b: any) => (b.views_count || 0) - (a.views_count || 0));
    }

    return result;
  }, [rawEvents, filters]);

  const hasActiveFilters = filters.category || filters.datePreset || filters.dateRange?.from || filters.priceMin || filters.priceMax || filters.modality !== "all" || filters.sort !== "date" || cityFilter;

  const clearAll = () => {
    setFilters({ ...defaultEventFilters });
    setCityFilter("");
  };

  return (
    <>
      <SEOHead
        title={t('eventsPage.seo.title')}
        description={t('eventsPage.seo.description')}
      />

      <BecomeProducerModal
        open={producerModalOpen}
        onOpenChange={setProducerModalOpen}
      />

      <div className="container pt-4 lg:pt-24 pb-16">
        {/* Breadcrumb */}
        <Breadcrumb className="mb-5 hidden lg:flex">
          <BreadcrumbList className="text-xs tracking-tight text-muted-foreground/80 sm:gap-2">
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/" className="inline-flex items-center gap-1.5 hover:text-foreground transition-colors">
                  <Home className="h-3.5 w-3.5 opacity-80" />
                  <span>{t('eventsPage.breadcrumb.home')}</span>
                </Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage className="font-medium text-foreground">
                {t('eventsPage.breadcrumb.current')}
              </BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        {/* Top section: Title + Filters */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
          <h1 className="font-display text-2xl lg:text-3xl font-semibold tracking-[-0.03em] shrink-0 text-foreground">
            {t('eventsPage.hero.title')}
          </h1>
          <div className="flex-1 min-w-0 lg:max-w-3xl flex lg:justify-end">
            <EventFilterBar filters={filters} onChange={setFilters} />
          </div>
        </div>

        {/* Search bar */}
        <div className="mb-5 max-w-2xl">
          <SearchInput
            type="text"
            placeholder={t('eventsPage.search.placeholder')}
            value={search}
            onChange={(e) => handleSearch(e.target.value)}
            className="bg-muted/50 border-border"
          />
          {search && (
            <Button variant="ghost" size="icon" className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7" onClick={() => setSearch("")}>
              <X className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>

        {/* City filter indicator */}
        {cityFilter && (
          <div className="inline-flex items-center gap-2 mb-5 px-3 py-1.5 rounded-full bg-muted/40 border border-border/60 text-xs text-muted-foreground">
            <MapPin className="h-3.5 w-3.5 text-primary" />
            <span>{t('eventsPage.cityBar.filterPrefix')} <span className="text-foreground font-semibold">{cityFilter}</span></span>
            <button onClick={() => setCityFilter("")} className="text-[11px] text-muted-foreground hover:text-foreground underline ml-1 cursor-pointer">
              {t('eventsPage.cityBar.clearFilter')}
            </button>
          </div>
        )}

        {/* City detection bar */}
        {!city && !cityFilter && (
          <button
            onClick={requestLocation}
            disabled={cityLoading}
            className="inline-flex items-center gap-2 mb-5 px-3.5 py-1.5 rounded-full border border-border/70 bg-card hover:bg-muted/50 text-xs text-muted-foreground hover:text-foreground transition-all active:scale-[0.98] cursor-pointer shadow-2xs"
          >
            <MapPin className="h-3.5 w-3.5 text-primary" />
            {cityLoading ? t('eventsPage.cityBar.detecting') : t('eventsPage.cityBar.requestLocation')}
          </button>
        )}
        {city && !cityFilter && (
          <div className="inline-flex items-center gap-2 mb-5 px-3 py-1.5 rounded-full bg-muted/40 border border-border/60 text-xs text-muted-foreground">
            <MapPin className="h-3.5 w-3.5 text-primary" />
            <span>{t('eventsPage.cityBar.detectedPrefix')} <span className="text-foreground font-semibold">{city}</span></span>
            <button onClick={() => { localStorage.removeItem("tickethall_detected_city"); window.location.reload(); }} className="text-[11px] text-muted-foreground hover:text-foreground underline ml-1 cursor-pointer">
              {t('eventsPage.cityBar.changeDetected')}
            </button>
          </div>
        )}

        {/* Result count + view toggle */}
        <div className="flex items-center justify-between mb-6 pt-1">
          <div className="flex items-center gap-3">
            {!isLoading && (
              <div className="inline-flex items-center gap-2 text-xs font-medium text-muted-foreground">
                <span className="h-1.5 w-1.5 rounded-full bg-primary shrink-0" />
                <span>{t('eventsPage.results.count', { count: filteredEvents.length })}</span>
              </div>
            )}
            {hasActiveFilters && (
              <button 
                onClick={clearAll} 
                className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted/70 transition-colors cursor-pointer"
              >
                <X className="h-3 w-3" /> {t('eventsPage.filters.clearAll')}
              </button>
            )}
          </div>
          <div className="flex items-center gap-2.5">
            {/* Seletor de layout: Grid ou Lista */}
            <div className="flex items-center border border-border/70 rounded-full p-1 bg-muted/35 shrink-0 shadow-2xs">
              <Button
                variant={viewMode === "grid" ? "secondary" : "ghost"}
                size="icon"
                className={cn(
                  "h-7 w-7 rounded-full p-0 transition-all",
                  viewMode === "grid" && "bg-background shadow-xs text-foreground"
                )}
                onClick={() => setViewMode("grid")}
                title={t('eventsPage.viewMode.gridTitle')}
              >
                <LayoutGrid className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant={viewMode === "list" ? "secondary" : "ghost"}
                size="icon"
                className={cn(
                  "h-7 w-7 rounded-full p-0 transition-all",
                  viewMode === "list" && "bg-background shadow-xs text-foreground"
                )}
                onClick={() => setViewMode("list")}
                title={t('eventsPage.viewMode.listTitle')}
              >
                <List className="h-3.5 w-3.5" />
              </Button>
            </div>
            <Button 
              size="sm" 
              className="gap-1.5 h-8 px-4 rounded-full text-xs font-semibold shadow-xs transition-transform active:scale-[0.96]" 
              onClick={handleCreateEvent}
            >
              <Plus className="h-3.5 w-3.5" /> {t('eventsPage.actions.createEvent')}
            </Button>
          </div>
        </div>

        {isLoading ? (
          <LoadingSkeleton variant="card" count={6} />
        ) : !filteredEvents || filteredEvents.length === 0 ? (
          <EmptyState
            icon={<Ticket className="h-12 w-12" />}
            title={t('eventsPage.emptyState.title')}
            description={t('eventsPage.emptyState.description')}
            actionLabel={hasActiveFilters ? t('eventsPage.emptyState.clearFiltersAction') : undefined}
            onAction={hasActiveFilters ? clearAll : undefined}
          />
        ) : (
          <div className="space-y-8">
            {/* Event list/grid container */}
            <div className={viewMode === "grid" ? "grid gap-2.5 sm:gap-4 grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5" : "flex flex-col gap-4"}>
              {filteredEvents.slice(0, visibleCount).map((event: any, index: number) => (
                <EventCard
                  key={event.id}
                  title={event.title}
                  date={formatDate(event.start_date, i18n.language, {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                  })}
                  startDate={event.start_date}
                  endDate={event.end_date}
                  venueName={event.venue_name}
                  isOnline={event.is_online}
                  city={event.venue_city || t('eventsPage.event.onlineFallback')}
                  imageUrl={event.cover_image_url}
                  priceFrom={event._minPrice ?? 0}
                  category={event.category}
                  slug={event.slug}
                  producerSlug={event.organizations?.slug || event.profiles?.organizer_slug}
                  isOrg={!!event.organization_id && !!event.organizations?.slug}
                  eventId={event.id}
                  priority={index < 4}
                  variant={viewMode}
                />
              ))}
            </div>

            {/* Load more */}
            {filteredEvents.length > visibleCount && (
              <div className="flex justify-center pt-8" ref={loadMoreRef}>
                <Button
                  variant="outline"
                  onClick={() => setVisibleCount((c) => c + PAGE_SIZE)}
                >
                  {t('eventsPage.actions.loadMore')}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    </>
  );
}
